import { useEffect, useState, useCallback, useMemo, type ReactNode } from 'react';
import type { AppSettings, Project, ProjectInput, ProjectWire, SectionReply } from '../types';
import { DEFAULT_SETTINGS, wireToProject, projectToBlob } from '../types';
import { type SectionKey } from '../prompts';
import { useAuth } from './useAuth';
import {
  AppContext,
  makeEmptySections,
  type GeneratedSection,
  type AppContextValue,
} from './AppContext';

const ACTIVE_ROOT = 'scalent-active-project';
const LEGACY_STORAGE_KEY = 'brand-framework-projects-v1';
const LEGACY_ACTIVE_KEY = 'brand-framework-active-project-v1';
const SETTINGS_KEY = 'scalent-settings-v1';
const ACTIVE_INTERVIEW_ROOT = 'scalent-active-interview';
const MIGRATION_FLAG_ROOT = 'scalent-projects-migrated';

function activeKey(namespace: string) {
  return `${ACTIVE_ROOT}-${namespace}`;
}

function activeInterviewKey(namespace: string) {
  return `${ACTIVE_INTERVIEW_ROOT}-${namespace}`;
}

function migrationFlagKey(namespace: string) {
  return `${MIGRATION_FLAG_ROOT}-${namespace}`;
}

function legacyProjectKey() {
  return LEGACY_STORAGE_KEY;
}

function legacyActiveKey() {
  return LEGACY_ACTIVE_KEY;
}

interface LegacyProject {
  id: string;
  name: string;
  input: ProjectInput;
  sections: Record<SectionKey, GeneratedSection>;
  createdAt: string;
  updatedAt: string;
}

async function fetchProjectsFromApi(): Promise<Project[]> {
  try {
    const res = await fetch('/api/projects');
    if (!res.ok) return [];
    const data = (await res.json()) as { projects: ProjectWire[] };
    return (data.projects || []).map(wireToProject);
  } catch {
    return [];
  }
}

async function postProjectToApi(project: Project): Promise<Project | null> {
  try {
    const res = await fetch('/api/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: project.name,
        idea: project.input.idea || project.name,
        industry: project.input.industry,
        location: project.input.location,
        teamSize: project.input.teamSize,
        stage: project.input.stage,
        goals: project.input.goals,
        locale: project.input.locale,
        blobJson: projectToBlob(project),
      }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { project: ProjectWire };
    return wireToProject(data.project);
  } catch {
    return null;
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const namespace = user?.id || 'anon';

  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProjectId, setActiveProjectIdState] = useState<string | null>(null);
  const [activeInterviewSessionId, setActiveInterviewSessionIdState] = useState<string | null>(null);
  const [legacyMigrationPending, setLegacyMigrationPending] = useState<string[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const [settings, setSettingsState] = useState<AppSettings>(() => {
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      return raw ? { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<AppSettings>) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  // Load projects from D1, plus a one-time migration from localStorage.
  useEffect(() => {
    let cancelled = false;
    setLoadingProjects(true);
    (async () => {
      // 1. One-time migration: read legacy localStorage projects and POST to /api/projects.
      try {
        const flag = localStorage.getItem(migrationFlagKey(namespace));
        if (flag !== 'done') {
          const legacyRaw = localStorage.getItem(legacyProjectKey());
          if (legacyRaw) {
            const parsed = JSON.parse(legacyRaw) as LegacyProject[];
            if (Array.isArray(parsed) && parsed.length > 0) {
              for (const p of parsed) {
                const migrated = await postProjectToApi({
                  id: p.id,
                  name: p.name,
                  input: p.input,
                  sections: p.sections || makeEmptySections(p.input.locale || 'en'),
                  createdAt: p.createdAt,
                  updatedAt: p.updatedAt,
                });
                if (!cancelled && migrated) {
                  // Don't replace local state — we'll fetch fresh below.
                }
              }
            }
            // Clear legacy keys.
            localStorage.removeItem(legacyProjectKey());
            localStorage.removeItem(legacyActiveKey());
          }
          localStorage.setItem(migrationFlagKey(namespace), 'done');
        }
      } catch {
        /* localStorage unavailable or JSON malformed */
      }

      // 2. Fetch from D1.
      const list = await fetchProjectsFromApi();
      if (cancelled) return;
      setProjects(list);

      // 3. Restore the active project from the namespace key.
      try {
        const raw = localStorage.getItem(activeKey(namespace));
        if (raw && list.some((p) => p.id === raw)) {
          setActiveProjectIdState(raw);
        } else {
          setActiveProjectIdState(null);
        }
      } catch {
        setActiveProjectIdState(null);
      }

      setLoadingProjects(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [namespace]);

  // Persist active project id.
  useEffect(() => {
    try {
      if (activeProjectId) localStorage.setItem(activeKey(namespace), activeProjectId);
      else localStorage.removeItem(activeKey(namespace));
    } catch {
      /* localStorage unavailable */
    }
  }, [activeProjectId, namespace]);

  // Active interview session id (resume hint).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(activeInterviewKey(namespace));
      setActiveInterviewSessionIdState(raw || null);
    } catch {
      setActiveInterviewSessionIdState(null);
    }
  }, [namespace]);

  // Legacy projects: any in-memory project the user has that does not yet have
  // a project-level interview session id. (Today, the blob_json field doesn't
  // carry interviewSessionId — that lives on the interview_sessions row — so
  // this is a soft heuristic that we just keep the list empty post-migration.)
  useEffect(() => {
    setLegacyMigrationPending([]);
  }, [namespace, projects]);

  const setActiveInterviewSessionId = (id: string | null) => {
    setActiveInterviewSessionIdState(id);
    try {
      if (id) localStorage.setItem(activeInterviewKey(namespace), id);
      else localStorage.removeItem(activeInterviewKey(namespace));
    } catch {
      /* localStorage unavailable */
    }
  };

  const markLegacyMigrationDone = () => {
    setLegacyMigrationPending([]);
  };

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      /* localStorage unavailable */
    }
  }, [settings]);

  const setSettings = (next: AppSettings) => {
    setSettingsState(next);
  };

  const setActiveProjectId = (id: string | null) => {
    setActiveProjectIdState(id);
  };

  const refreshProjects = useCallback(async () => {
    const list = await fetchProjectsFromApi();
    setProjects(list);
  }, []);

  const createProject = useCallback(
    async (name: string, input: ProjectInput): Promise<Project> => {
      const localShape: Project = {
        id: crypto.randomUUID(),
        name,
        input,
        sections: makeEmptySections(input.locale || 'en'),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const created = await postProjectToApi(localShape);
      const final = created || localShape;
      setProjects((prev) => [final, ...prev]);
      setActiveProjectIdState(final.id);
      return final;
    },
    []
  );

  const updateProject = useCallback(
    async (project: Project): Promise<void> => {
      // Optimistic update.
      setProjects((prev) =>
        prev.map((p) => (p.id === project.id ? { ...project, updatedAt: new Date().toISOString() } : p))
      );
      try {
        const res = await fetch(`/api/projects/${project.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: project.name,
            idea: project.input.idea,
            industry: project.input.industry,
            location: project.input.location,
            teamSize: project.input.teamSize,
            stage: project.input.stage,
            goals: project.input.goals,
            locale: project.input.locale,
            blobJson: projectToBlob(project),
          }),
        });
        if (res.ok) {
          const data = (await res.json()) as { project: ProjectWire };
          setProjects((prev) =>
            prev.map((p) => (p.id === project.id ? wireToProject(data.project) : p))
          );
        }
      } catch {
        /* keep optimistic state */
      }
    },
    []
  );

  const deleteProject = useCallback(
    async (id: string): Promise<void> => {
      setProjects((prev) => prev.filter((p) => p.id !== id));
      if (activeProjectId === id) setActiveProjectIdState(null);
      try {
        await fetch(`/api/projects/${id}`, { method: 'DELETE' });
      } catch {
        /* keep optimistic state */
      }
    },
    [activeProjectId]
  );

  const updateSection = useCallback(
    async (
      projectId: string,
      key: SectionKey,
      content: string,
      status: GeneratedSection['status'],
      error?: string
    ): Promise<void> => {
      let next: Project | null = null;
      setProjects((prev) =>
        prev.map((p) => {
          if (p.id !== projectId) return p;
          const updated: Project = {
            ...p,
            updatedAt: new Date().toISOString(),
            sections: {
              ...p.sections,
              [key]: {
                ...p.sections[key],
                content,
                status,
                error,
                generatedAt: status === 'done' ? new Date().toISOString() : p.sections[key]?.generatedAt,
              },
            },
          };
          next = updated;
          return updated;
        })
      );
      if (next) {
        try {
          const res = await fetch(`/api/projects/${projectId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ blobJson: projectToBlob(next) }),
          });
          if (res.ok) {
            const data = (await res.json()) as { project: ProjectWire };
            setProjects((prev) =>
              prev.map((p) => (p.id === projectId ? wireToProject(data.project) : p))
            );
          }
        } catch {
          /* keep optimistic state */
        }
      }
    },
    []
  );

  const addSectionReply = useCallback(
    async (projectId: string, key: SectionKey, reply: SectionReply): Promise<void> => {
      let next: Project | null = null;
      setProjects((prev) =>
        prev.map((p) => {
          if (p.id !== projectId) return p;
          const section = p.sections[key];
          const replies = section.replies ? [...section.replies, reply] : [reply];
          const updated: Project = {
            ...p,
            updatedAt: new Date().toISOString(),
            sections: {
              ...p.sections,
              [key]: { ...section, replies },
            },
          };
          next = updated;
          return updated;
        })
      );
      if (next) {
        try {
          const res = await fetch(`/api/projects/${projectId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ blobJson: projectToBlob(next) }),
          });
          if (res.ok) {
            const data = (await res.json()) as { project: ProjectWire };
            setProjects((prev) =>
              prev.map((p) => (p.id === projectId ? wireToProject(data.project) : p))
            );
          }
        } catch {
          /* keep optimistic state */
        }
      }
    },
    []
  );

  const value = useMemo<AppContextValue>(
    () => ({
      projects,
      activeProjectId,
      setActiveProjectId,
      createProject,
      updateProject,
      deleteProject,
      updateSection,
      addSectionReply,
      refreshProjects,
      activeInterviewSessionId,
      setActiveInterviewSessionId,
      legacyMigrationPending,
      markLegacyMigrationDone,
      settings,
      setSettings,
      loadingProjects,
    }),
    [
      projects,
      activeProjectId,
      activeInterviewSessionId,
      legacyMigrationPending,
      settings,
      loadingProjects,
      createProject,
      updateProject,
      deleteProject,
      updateSection,
      addSectionReply,
      refreshProjects,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export type { AppContextValue, GeneratedSection };
